import { set } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { capitalCaseFormatter } from '../../utils/stringFormatters';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { amountCheckFn } from '../../utils/commonUtil';
import EditIcon from '../../components/Icons/EditIcon';
import Loader from '../../components/Loader';
import PhoneField from '../../components/PhoneField';
export default function AccountInfo({ setStep, accountData, setAccountData, loader }) {
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [showBillingContact, setShowBillingContact] = useState(false);
  const services = React.useMemo(() => {
    // Prefer detailed address-level assignments when available
    const addressLevelRaw = [
      ...(accountData?.service_addresses?.flatMap((sa) =>
        (sa?.services || []).map((service) => ({
          name: service?.name || '-',
          unit: service?.unit_type?.toLowerCase?.() || service?.unit?.toLowerCase?.() || 'item',
          standard: service?.standard_price ?? service?.default_client_charge ?? service?.price ?? null,
          priority: service?.priority_fee ?? service?.default_priority_client_charge ?? service?.priority_price ?? null,
          addressName: sa?.address?.name || '-',
        }))
      ) || []),
      ...(accountData?.addresses?.flatMap((addr) =>
        (addr?.services || []).map((service) => ({
          name: service?.name || '-',
          unit: service?.unit_type?.toLowerCase?.() || service?.unit?.toLowerCase?.() || 'item',
          standard: service?.standard_price ?? service?.default_client_charge ?? service?.price ?? null,
          priority: service?.priority_fee ?? service?.default_priority_client_charge ?? service?.priority_price ?? null,
          addressName: addr?.name || '-',
        }))
      ) || []),
    ];

    let raw = addressLevelRaw;
    if (!raw.length) {
      const allAddressNames = (accountData?.addresses || []).map((a) => a?.name).filter(Boolean);
      const joined = allAddressNames.length ? allAddressNames.join(', ') : '-';
      raw = (accountData?.services || []).map((s) => ({
        name: s?.name || '-',
        unit: s?.unit_type?.toLowerCase?.() || s?.unit?.toLowerCase?.() || 'item',
        standard: s?.standard_price ?? s?.default_client_charge ?? s?.price ?? null,
        priority: s?.priority_fee ?? s?.default_priority_client_charge ?? s?.priority_price ?? null,
        addressName: joined,
      }));
    }

    // Group by address name — each address keeps its own services (no dedup across addresses)
    const byAddress = {};
    raw.forEach((item) => {
      const addrKey = item.addressName;
      if (!byAddress[addrKey]) byAddress[addrKey] = [];
      byAddress[addrKey].push({
        name: item.name,
        standardPrice: item.standard != null ? `$${item.standard} /${item.unit}` : '-',
        priorityFee: item.priority != null ? `+$${item.priority} /${item.unit}` : '-',
      });
    });

    return Object.entries(byAddress).map(([addressName, items]) => ({
      addressName,
      items,
    }));
  }, [accountData?.service_addresses, accountData?.services, accountData?.addresses]);

  // Consistent style tokens
  const labelCls = 'mb-1 text-sm font-medium text-gray-400';
  const valueCls = 'text-base font-semibold text-[#1e3a5f]';
  const inputCls =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-semibold text-[#1e3a5f] focus:border-blue-500 focus:outline-none';
  const cardCls = 'rounded-2xl bg-white px-6 py-6 shadow-lg';
  const headingCls = 'mb-5 text-lg font-bold text-[#1e3a5f]';
  const dividerCls = 'border-t border-gray-200';

  return (
    <div className='relative mx-auto min-h-screen w-full space-y-5 px-2 pb-6 pt-4'>
      {/* Header */}
      <div className='mb-4 flex items-center justify-center text-center'>
        <button
          className='flex items-center justify-center gap-2 text-lg font-semibold text-white'
          onClick={() => setStep && setStep((prev) => prev + 1)}
        >
          Review details and continue
          <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>

      {/* ──── Account Info ──── */}
      <div className={cardCls}>
        <div className='mb-5 flex items-center justify-between'>
          <h2 className={headingCls + ' !mb-0'}>Account Info</h2>
          <button className='text-blue-500 hover:text-blue-600' onClick={() => setIsEditingAccount(!isEditingAccount)}>
            <EditIcon />
          </button>
        </div>
        {loader ? (
          <Loader classes='h-32' />
        ) : (
          <div className='space-y-4'>
            <div>
              <p className={labelCls}>Business Name</p>
              {isEditingAccount ? (
                <input
                  type='text'
                  value={accountData?.organization_name}
                  onChange={(e) => setAccountData({ ...accountData, organization_name: e.target.value })}
                  className={inputCls}
                />
              ) : (
                <p className={valueCls}>{accountData?.organization_name}</p>
              )}
            </div>

            <div>
              <p className={labelCls}>First Name</p>
              {isEditingAccount ? (
                <input
                  type='text'
                  value={accountData?.first_name}
                  onChange={(e) => setAccountData({ ...accountData, first_name: e.target.value })}
                  className={inputCls}
                />
              ) : (
                <p className={valueCls}>{accountData?.first_name}</p>
              )}
            </div>

            <div>
              <p className={labelCls}>Last Name</p>
              {isEditingAccount ? (
                <input
                  type='text'
                  value={accountData?.last_name}
                  onChange={(e) => setAccountData({ ...accountData, last_name: e.target.value })}
                  className={inputCls}
                />
              ) : (
                <p className={valueCls}>{accountData?.last_name}</p>
              )}
            </div>

            <div>
              <p className={labelCls}>Phone Number</p>
              {isEditingAccount ? (
                <PhoneField
                  required
                  name={'orgAdminPhoneNumber'}
                  value={accountData?.phone_numbers?.[0]?.phone_number}
                  onChange={(e, apiValue) => {
                    const phoneNumber = apiValue || e.target.value;
                    setAccountData({ ...accountData, phone_numbers: [{ phone_number: phoneNumber }] });
                  }}
                />
              ) : (
                <p className={valueCls}>{accountData?.phone_numbers?.[0]?.phone_number}</p>
              )}
            </div>

            {accountData?.billing_type === 'SEPARATE' && (
              <>
                <button
                  type='button'
                  onClick={() => setShowBillingContact((v) => !v)}
                  className='flex items-center gap-2 text-sm font-medium text-blue-500 hover:text-blue-600'
                >
                  <PlusCircleIcon className='h-5 w-5' />
                  {showBillingContact ? 'Hide billing contact' : 'Add a billing contact'}
                </button>

                {showBillingContact && (
                  <div className='space-y-4 pt-2'>
                    <div>
                      <p className={labelCls}>Billing First Name (Optional)</p>
                      <input
                        type='text'
                        value={accountData?.billing_contact_first_name}
                        onChange={(e) => setAccountData({ ...accountData, billing_contact_first_name: e.target.value })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <p className={labelCls}>Billing Last Name (Optional)</p>
                      <input
                        type='text'
                        value={accountData?.billing_contact_last_name}
                        onChange={(e) => setAccountData({ ...accountData, billing_contact_last_name: e.target.value })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <p className={labelCls}>Billing Email (Optional)</p>
                      <input
                        type='email'
                        value={accountData?.billing_contact_email}
                        onChange={(e) => setAccountData({ ...accountData, billing_contact_email: e.target.value })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <p className={labelCls}>Billing Phone Number (Optional)</p>
                      <PhoneField
                        required
                        name={'billingContactPhoneNumber'}
                        value={accountData?.billing_contact_phone_number?.[0]?.phone_number}
                        onChange={(e, apiValue) => {
                          const phoneNumber = apiValue || e.target.value;
                          setAccountData({
                            ...accountData,
                            billing_contact_phone_number: [{ phone_number: phoneNumber }],
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ──── Billing Info (non-SEPARATE) ──── */}
      {accountData?.billing_type !== 'SEPARATE' && (
        <div className={cardCls}>
          <h2 className={headingCls}>Billing Info</h2>
          {loader ? (
            <Loader classes='h-32' />
          ) : (
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <p className={labelCls}>Sales Tax</p>
                  <p className={valueCls}>{capitalCaseFormatter(accountData?.sales_tax_status)}</p>
                </div>
                <div>
                  <p className={labelCls}>Order Minimum Charge</p>
                  <p className={valueCls}>{amountCheckFn(accountData?.order_minimum_charge)}</p>
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <p className={labelCls}>Billing Frequency</p>
                  <p className={valueCls}>{capitalCaseFormatter(accountData?.invoice_frequency)}</p>
                </div>
                <div>
                  <p className={labelCls}>Billing Day</p>
                  <p className={valueCls}>{accountData?.invoice_day_of_month || '-'}</p>
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <p className={labelCls}>Paying Via</p>
                  <p className={valueCls}>
                    {accountData?.default_payment_method_type?.toLowerCase() === 'stripe'
                      ? 'Credit Card'
                      : capitalCaseFormatter(accountData?.default_payment_method_type)}
                  </p>
                </div>
                <div>
                  <p className={labelCls}>Payment Terms</p>
                  <p className={valueCls}>{capitalCaseFormatter(accountData?.payment_terms)}</p>
                </div>
              </div>

              <button
                type='button'
                onClick={() => setShowBillingContact((v) => !v)}
                className='flex items-center gap-2 text-sm font-medium text-blue-500 hover:text-blue-600'
              >
                <PlusCircleIcon className='h-5 w-5' />
                {showBillingContact ? 'Hide billing contact' : 'Add a billing contact'}
              </button>

              {showBillingContact && (
                <div className='space-y-4 pt-2'>
                  <div>
                    <p className={labelCls}>Billing First Name (Optional)</p>
                    <input
                      type='text'
                      value={accountData?.billing_contact_first_name}
                      onChange={(e) => setAccountData({ ...accountData, billing_contact_first_name: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <p className={labelCls}>Billing Last Name (Optional)</p>
                    <input
                      type='text'
                      value={accountData?.billing_contact_last_name}
                      onChange={(e) => setAccountData({ ...accountData, billing_contact_last_name: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <p className={labelCls}>Billing Email (Optional)</p>
                    <input
                      type='email'
                      value={accountData?.billing_contact_email}
                      onChange={(e) => setAccountData({ ...accountData, billing_contact_email: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <p className={labelCls}>Billing Phone Number (Optional)</p>
                    <PhoneField
                      required
                      name={'billingContactPhoneNumber'}
                      value={accountData?.billing_contact_phone_number?.[0]?.phone_number}
                      onChange={(e, apiValue) => {
                        const phoneNumber = apiValue || e.target.value;
                        setAccountData({
                          ...accountData,
                          billing_contact_phone_number: [{ phone_number: phoneNumber }],
                        });
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ──── Pickup & Delivery ──── */}
      <div className={cardCls}>
        <h2 className={headingCls}>Pickup & Delivery</h2>
        {loader ? (
          <Loader classes='h-32' />
        ) : (
          <div className='space-y-4'>
            {accountData?.addresses?.length > 0 ? (
              accountData?.addresses?.map((addr, index) => (
                <div key={index}>
                  {index > 0 && <div className={'my-4 ' + dividerCls} />}
                  <div className='space-y-3'>
                    <div>
                      <p className={labelCls}>Address Name</p>
                      <p className={valueCls}>{addr?.name}</p>
                    </div>
                    <div>
                      <p className={labelCls}>Address</p>
                      <p className={valueCls}>
                        {[addr?.address_line_1, addr?.address_line_2].filter(Boolean).join(', ')}
                        <br />
                        {[addr?.city, [addr?.state, addr?.postal_code].filter(Boolean).join(' ')]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>

                    {accountData?.billing_type === 'SEPARATE' && addr?.billing_details && (
                      <>
                        <div>
                          <p className={labelCls}>Order Minimum Charge</p>
                          <p className={valueCls}>{amountCheckFn(addr?.billing_details?.order_minimum_charge)}</p>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <p className={labelCls}>Billing Frequency</p>
                            <p className={valueCls}>{capitalCaseFormatter(addr?.billing_details?.invoice_frequency)}</p>
                          </div>
                          <div>
                            <p className={labelCls}>Billing Day</p>
                            <p className={valueCls}>
                              {capitalCaseFormatter(addr?.billing_details?.invoice_day_of_week) ||
                                addr?.billing_details?.invoice_day_of_month ||
                                '-'}
                            </p>
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <p className={labelCls}>Paying Via</p>
                            <p className={valueCls}>
                              {addr?.billing_details?.default_payment_method_type?.toLowerCase() === 'stripe'
                                ? 'Credit Card'
                                : capitalCaseFormatter(addr?.billing_details?.default_payment_method_type)}
                            </p>
                          </div>
                          <div>
                            <p className={labelCls}>Payment Terms</p>
                            <p className={valueCls}>{capitalCaseFormatter(addr?.billing_details?.payment_terms)}</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className={valueCls}>-</p>
            )}
          </div>
        )}
      </div>

      {/* ──── Services ──── */}
      <div className={cardCls}>
        <h2 className={headingCls}>Services</h2>
        {loader ? (
          <Loader classes='h-32' />
        ) : (
          <div className='space-y-4'>
            {services.length > 0 ? (
              services.map((group, gi) => (
                <div key={gi}>
                  {gi > 0 && <div className={'my-4 ' + dividerCls} />}
                  <p className={labelCls}>Address Name</p>
                  <p className={valueCls + ' mb-3'}>{group.addressName}</p>
                  <table className='w-full'>
                    <thead>
                      <tr>
                        <th className='pb-2 text-left text-sm font-medium text-gray-400'>Service</th>
                        <th className='pb-2 text-right text-sm font-medium text-gray-400'>Pricing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((svc, si) => (
                        <tr key={si}>
                          <td className='py-2.5 align-top text-base font-semibold text-[#1e3a5f]'>{svc.name}</td>
                          <td className='py-2.5 text-right align-top'>
                            <span className='text-base font-semibold text-[#1e3a5f]'>{svc.standardPrice}</span>
                            <br />
                            <span className='whitespace-nowrap text-sm text-gray-400'>Priority {svc.priorityFee}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            ) : (
              <p className={valueCls}>-</p>
            )}
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div className='mb-4 flex items-center justify-center text-center'>
        <button
          className='flex items-center justify-center gap-2 text-lg font-semibold text-white'
          onClick={() => setStep && setStep((prev) => prev + 1)}
        >
          Next
          <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>
    </div>
  );
}
